import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import "./AdminApplicationDetail.css";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CompareArrowsOutlinedIcon from "@mui/icons-material/CompareArrowsOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import useAdminHttp from "../../../hooks/use-admin-http";
import AdminApplicationDocuments from "./AdminApplicationDocuments";
import AdminApplicationCrossChecks, {
  crossCheckCounts,
} from "./AdminApplicationCrossChecks";
import WLCustomModal from "../modals/WLCustomModal";
import propertyPhoto from "../../../img/module-housing-rent.jpg";
import nationalities from "../../../utils/nationalities.json";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8000/api/v1";

// Public applicant-facing page where this application can be viewed.
const APPLICATION_VIEW_URL = "https://welocate.checkyourterms.com/viewing_application";

// How often to refetch the application while a document prescan runs.
const VALIDATION_POLL_MS = 3000;

const PROFILE_LABELS = {
  employed: "Employed",
  student: "Student",
  "self-employed": "Self-employed",
};

const NATIONALITY_NAMES = Object.fromEntries(
  nationalities.map((n) => [
    n.t_client_nationality_id,
    n.t_client_nationality_name.trim(),
  ])
);

// Nationality arrives as a t_client_nationality_id; older records may still
// hold the plain name, so fall back to showing the raw value.
const nationalityLabel = (value) =>
  value == null || value === ""
    ? null
    : NATIONALITY_NAMES[Number(value)] || String(value);

const formatEuro = (value) =>
  value == null
    ? null
    : new Intl.NumberFormat("nl-NL", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      }).format(value);

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date)) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`;
};

const listingAddress = (listing) => {
  if (!listing) return null;
  const street = [listing.street, listing.house_number, listing.floor]
    .filter(Boolean)
    .join(" ");
  return [street, listing.city].filter(Boolean).join(", ") || null;
};

const contractLengthLabel = (value) => {
  if (!value) return null;
  if (value === "permanent") return "Permanent contract";
  return `${value} months contract`;
};

const PRIORITY_OPTIONS = ["1", "2", "3", "4", "5"].map((value) => ({
  value,
  label: value,
}));

// Team member ids as used by the backend's assigned_to field.
const ASSIGNEE_OPTIONS = [
  { id: 68, name: "Abiel Bramaditya" },
  { id: 71, name: "Anja Gulden" },
  { id: 69, name: "Ayca Gulsen" },
  { id: 54, name: "Bart Ravelli" },
  { id: 67, name: "Gonzalo Benitez" },
  { id: 80, name: "Joy van Geesink" },
  { id: 77, name: "Justin Lucaci" },
  { id: 66, name: "Nazgol Khatibi" },
  { id: 78, name: "Paul Minde" },
  { id: 44, name: "Sam England" },
].map(({ id, name }) => ({ value: String(id), label: name }));

// Status codes as used by the backend.
const STATUS_OPTIONS = [
  { value: "0", label: "Not started" },
  { value: "1", label: "In progress" },
  { value: "2", label: "Reviewed" },
  { value: "3", label: "Potential" },
  { value: "4", label: "Proposed" },
  { value: "5", label: "Rejected" },
  { value: "6", label: "Accepted" },
  { value: "7", label: "Cancelled" },
];

const STATUS_NOT_STARTED = "0";

// Backend column limit for the application's notes field.
const COMMENT_MAX_LENGTH = 255;

const ASK_APPLICATION_SUGGESTIONS = [
  "Anything to flag?",
  "Does the income match the documents?",
  "Can they cover the deposit?",
  "Why do they want this place?",
  "What kind of employment contract?",
];

// Right-hand drawer where the admin can ask questions about the whole
// application (form + every uploaded document). The thread lives in the
// parent so closing and reopening the drawer keeps the conversation.
const AskApplicationDrawer = ({
  applicationId,
  applicantName,
  firstName,
  docCount,
  thread,
  onAppend,
  onClose,
}) => {
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(null);
  const threadRef = useRef(null);
  const inputRef = useRef(null);
  const { isLoading, error, sendRequest } = useAdminHttp();

  const ask = (question, { echo = true } = {}) => {
    const trimmed = question.trim();
    if (!trimmed || isLoading) return;
    if (echo) onAppend({ role: "user", text: trimmed });
    setPending(trimmed);
    setInput("");
    sendRequest(
      {
        url: `${BACKEND_URL}/welocate/applications/${applicationId}/ask`,
        method: "POST",
        body: { question: trimmed },
      },
      (data) => {
        onAppend({
          role: "assistant",
          text: data.answer,
          documents: data.application_document_uploads,
        });
        setPending(null);
      }
    );
  };

  useEffect(() => {
    if (error && pending != null) {
      onAppend({ role: "error", text: error, question: pending });
      setPending(null);
    }
  }, [error, pending, onAppend]);

  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread.length, isLoading]);

  useEffect(() => {
    inputRef.current?.focus();
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const handleSubmit = (e) => {
    e.preventDefault();
    ask(input);
  };

  return (
    <>
      <div className="wlad-drawer-overlay" onClick={onClose} />
      <aside className="wlad-drawer" role="dialog" aria-modal="true">
        <div className="wlad-drawer-header">
          <span className="wlad-drawer-icon">
            <SearchIcon />
          </span>
          <div className="wlad-drawer-titles">
            <h2 className="wlad-drawer-title">Ask this application</h2>
            <p className="wlad-drawer-sub">
              {[
                applicantName,
                `form + ${docCount} document${docCount === 1 ? "" : "s"}`,
                "answers cite their sources",
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
          <button
            type="button"
            className="wlad-drawer-close"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="wlad-drawer-chat">
          <div className="wlad-ask-thread" ref={threadRef}>
            {thread.length === 0 && !isLoading ? (
              <div className="wlad-ask-empty">
                <h3 className="wlad-ask-empty-title">
                  Ask anything about {firstName ? `${firstName}'s` : "this"}{" "}
                  application
                </h3>
                <p className="wlad-ask-empty-sub">
                  Answers are grounded in the application form and all uploaded
                  documents, and cite their source.
                </p>
              </div>
            ) : (
              <>
                {thread.map((message, i) =>
                  message.role === "error" ? (
                    <div className="wlad-ask-msg wlad-ask-msg--error" key={i}>
                      {message.text}{" "}
                      <button
                        type="button"
                        className="wlad-doc-retry"
                        onClick={() => ask(message.question, { echo: false })}
                      >
                        Try again
                      </button>
                    </div>
                  ) : (
                    <div
                      className={`wlad-ask-msg wlad-ask-msg--${message.role}`}
                      key={i}
                    >
                      {message.text}
                      {message.documents?.length > 0 && (
                        <div className="wlad-ask-sources">
                          {message.documents.map((doc, j) => (
                            <span className="wlad-ask-source" key={doc.id || j}>
                              <AttachFileOutlinedIcon />
                              {doc.filename || doc.document_slug}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                )}
                {isLoading && (
                  <div className="wlad-ask-msg wlad-ask-msg--assistant wlad-ask-typing">
                    <span />
                    <span />
                    <span />
                  </div>
                )}
              </>
            )}
          </div>

          <div className="wlad-ask-suggestions">
            {ASK_APPLICATION_SUGGESTIONS.map((suggestion) => (
              <button
                type="button"
                className="wlad-ask-suggestion"
                key={suggestion}
                onClick={() => ask(suggestion)}
              >
                {suggestion}
              </button>
            ))}
          </div>

          <form className="wlad-ask-bar" onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              className="wlad-ask-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="e.g. Is there anything inconsistent across this application?"
            />
            <button
              type="submit"
              className="wlad-ask-send"
              disabled={!input.trim() || isLoading}
            >
              Ask
            </button>
          </form>
        </div>
      </aside>
    </>
  );
};

const TriageField = ({
  label,
  value,
  options,
  placeholder,
  onChange,
  disabled,
}) => (
  <label className="wlad-triage-field">
    <span className="wlad-triage-label">{label}</span>
    <select
      className="wlad-triage-select"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    >
      {placeholder != null && <option value="">{placeholder}</option>}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </label>
);

const DetailRow = ({ label, children }) => (
  <div className="wlad-row">
    <span className="wlad-row-label">{label}</span>
    <span className="wlad-row-value">{children}</span>
  </div>
);

const AdminApplicationDetail = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const { setApplicantName } = useOutletContext() || {};
  const [application, setApplication] = useState(null);
  const [activeTab, setActiveTab] = useState("application");
  // Review state per document upload, keyed by upload id. Seeded from the
  // application payload and updated optimistically when the admin reviews.
  const [reviews, setReviews] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // Triage fields; empty string means "not set" and is sent as null.
  const [priority, setPriority] = useState("");
  const [assignee, setAssignee] = useState("");
  const [status, setStatus] = useState(STATUS_NOT_STARTED);
  // Transient save feedback for the triage row: "saved" | "error" | null.
  const [triageFeedback, setTriageFeedback] = useState(null);
  const triageFeedbackTimer = useRef(null);
  // Free-text admin comment, persisted on the application's notes field.
  const [comment, setComment] = useState("");
  const [savedComment, setSavedComment] = useState("");
  // Transient save feedback for the comment tab: "saved" | "error" | null.
  const [commentFeedback, setCommentFeedback] = useState(null);
  const commentFeedbackTimer = useRef(null);
  const [showAskDrawer, setShowAskDrawer] = useState(false);
  // Ask-this-application conversation, kept here so closing the drawer
  // does not throw the thread away.
  const [askThread, setAskThread] = useState([]);
  const appendAskMessage = useCallback((message) => {
    setAskThread((prev) => [...prev, message]);
  }, []);
  const closeAskDrawer = useCallback(() => setShowAskDrawer(false), []);
  const { isLoading, error, sendRequest } = useAdminHttp();
  const {
    isLoading: isDeleting,
    error: deleteError,
    sendRequest: sendDeleteRequest,
  } = useAdminHttp();
  const { sendRequest: sendReviewRequest } = useAdminHttp();
  // Separate instance so a background refetch (after an admin upload) does
  // not flip the page-level loading state and unmount the documents panel.
  const { sendRequest: sendRefetchRequest } = useAdminHttp();
  // Triage data (priority, assignee, status, notes) lives behind its own
  // protected endpoint, separate from the application itself.
  const { sendRequest: sendTriageFetchRequest } = useAdminHttp();
  const {
    isLoading: isTriageSaving,
    error: triageError,
    sendRequest: sendTriageRequest,
  } = useAdminHttp();
  const {
    isLoading: isCommentSaving,
    error: commentError,
    sendRequest: sendCommentRequest,
  } = useAdminHttp();

  // Optimistically apply a triage change and persist it. The backend takes a
  // partial PATCH on the application with priority, assigned_to and status
  // as numbers (or null to unset).
  const handleTriageChange = (field, value) => {
    const setters = {
      priority: setPriority,
      assigned_to: setAssignee,
      status: setStatus,
    };
    setters[field](value);
    setTriageFeedback(null);
    sendTriageRequest(
      {
        url: `${BACKEND_URL}/welocate/applications/${applicationId}/triage`,
        method: "PATCH",
        body: {
          [field]: value === "" ? null : Number(value),
        },
      },
      () => {
        setTriageFeedback("saved");
        clearTimeout(triageFeedbackTimer.current);
        triageFeedbackTimer.current = setTimeout(
          () => setTriageFeedback(null),
          2500
        );
      }
    );
  };

  useEffect(() => {
    if (triageError) setTriageFeedback("error");
  }, [triageError]);

  useEffect(() => () => clearTimeout(triageFeedbackTimer.current), []);

  // Persists the comment with the same partial PATCH as the triage fields.
  const handleCommentSave = () => {
    const trimmed = comment.trim();
    setCommentFeedback(null);
    sendCommentRequest(
      {
        url: `${BACKEND_URL}/welocate/applications/${applicationId}/triage`,
        method: "PATCH",
        body: { notes: trimmed === "" ? null : trimmed },
      },
      () => {
        setSavedComment(trimmed);
        setComment(trimmed);
        setCommentFeedback("saved");
        clearTimeout(commentFeedbackTimer.current);
        commentFeedbackTimer.current = setTimeout(
          () => setCommentFeedback(null),
          2500
        );
      }
    );
  };

  useEffect(() => {
    if (commentError) setCommentFeedback("error");
  }, [commentError]);

  useEffect(() => () => clearTimeout(commentFeedbackTimer.current), []);

  const handleReview = (uploadId, status) => {
    setReviews((prev) => ({ ...prev, [uploadId]: status }));
    sendReviewRequest(
      {
        url: `${BACKEND_URL}/welocate/applications/${applicationId}/document-uploads/${uploadId}/review`,
        method: "PATCH",
        body: { status },
      },
      () => {}
    );
  };

  // Reloads the application in place, keeping the active tab and any review
  // statuses chosen in this session. Used after an admin uploads or deletes
  // a document, and while polling during a prescan.
  const refetchApplication = useCallback(() => {
    sendRefetchRequest(
      { url: `${BACKEND_URL}/welocate/applications/${applicationId}` },
      (data) => {
        setApplication(data);
        setReviews((prev) => {
          const next = { ...prev };
          (data.document_uploads || []).forEach((upload) => {
            if (
              !next[upload.id] &&
              (upload.status === "approved" || upload.status === "flagged")
            ) {
              next[upload.id] = upload.status;
            }
          });
          return next;
        });
      }
    );
  }, [applicationId, sendRefetchRequest]);

  const handleDelete = () => {
    sendDeleteRequest(
      {
        url: `${BACKEND_URL}/welocate/applications/${applicationId}`,
        method: "DELETE",
      },
      () => navigate("/admin/applications")
    );
  };

  useEffect(() => {
    setActiveTab("application");
    setReviews({});
    setAskThread([]);
    setShowAskDrawer(false);
    setPriority("");
    setAssignee("");
    setStatus(STATUS_NOT_STARTED);
    setTriageFeedback(null);
    setComment("");
    setSavedComment("");
    setCommentFeedback(null);
    sendRequest(
      { url: `${BACKEND_URL}/welocate/applications/${applicationId}` },
      (data) => {
        setApplication(data);
        // Seed review statuses persisted on earlier visits.
        const seeded = {};
        (data.document_uploads || []).forEach((upload) => {
          if (upload.status === "approved" || upload.status === "flagged") {
            seeded[upload.id] = upload.status;
          }
        });
        setReviews(seeded);
      }
    );
  }, [applicationId, sendRequest]);

  // Seed the triage fields persisted on earlier visits. Fetched separately
  // so a missing or forbidden triage record does not block the page.
  useEffect(() => {
    sendTriageFetchRequest(
      { url: `${BACKEND_URL}/welocate/applications/${applicationId}/triage` },
      (data) => {
        if (!data) return;
        setPriority(data.priority != null ? String(data.priority) : "");
        setAssignee(data.assigned_to != null ? String(data.assigned_to) : "");
        setStatus(
          data.status != null ? String(data.status) : STATUS_NOT_STARTED
        );
        setComment(data.notes || "");
        setSavedComment(data.notes || "");
      }
    );
  }, [applicationId, sendTriageFetchRequest]);

  const details = application?.details || {};
  const offer = application?.offer || {};
  const household = application?.household || {};
  const employment = application?.employment;
  const uploads = useMemo(
    () => application?.document_uploads || [],
    [application]
  );

  // While the AI prescan is still running for any document, poll the
  // application so the results appear without a manual reload. The
  // interval clears itself once no upload is validating any more.
  const validationInProgress = uploads.some((u) => u.validation_in_progress);
  useEffect(() => {
    if (!validationInProgress) return undefined;
    const timer = setInterval(refetchApplication, VALIDATION_POLL_MS);
    return () => clearInterval(timer);
  }, [validationInProgress, refetchApplication]);
  const crossChecks = useMemo(
    () => application?.cross_document_check_records || [],
    [application]
  );
  const crossCheckTotals = useMemo(
    () => crossCheckCounts(crossChecks),
    [crossChecks]
  );

  // Aggregated AI pre-scan outcome across every validation record of every
  // upload. A failed rule counts under its severity; PASSED counts as passed.
  const prescanCounts = useMemo(() => {
    const counts = { blocking: 0, flagged: 0, note: 0, passed: 0, total: 0 };
    uploads.forEach((upload) => {
      (upload.validation_records || []).forEach((record) => {
        counts.total += 1;
        if (record.status === "PASSED") counts.passed += 1;
        else if (record.validation_rule?.severity === "FLAG") counts.flagged += 1;
        else if (record.validation_rule?.severity === "NOTE") counts.note += 1;
        else counts.blocking += 1;
      });
    });
    return counts;
  }, [uploads]);

  const fullName =
    [details.first_name, details.last_name].filter(Boolean).join(" ") || null;

  useEffect(() => {
    if (fullName && setApplicantName) setApplicantName(fullName);
  }, [fullName, setApplicantName]);

  if (isLoading) {
    return <div className="wlad-detail wlad-message">Loading application...</div>;
  }
  if (error) {
    return <div className="wlad-detail wlad-message">{error}</div>;
  }
  if (!application) {
    return null;
  }

  const listing = application.listing;
  const asking = listing?.monthly_rent;

  const listingId = application.listing_id ?? listing?.id;
  const applicationUrl = listingId
    ? `${APPLICATION_VIEW_URL}/${listingId}?applicationId=${applicationId}`
    : null;

  const listingMeta =
    listing &&
    [
      [listing.postcode, listing.city].filter(Boolean).join(" "),
      listing.area != null ? `${listing.area} m²` : null,
      listing.bedrooms != null
        ? `${listing.bedrooms} bedroom${listing.bedrooms === 1 ? "" : "s"}`
        : null,
      listing.is_furnished == null
        ? null
        : listing.is_furnished
        ? "Furnished"
        : "Unfurnished",
    ]
      .filter(Boolean)
      .join(" · ");

  const yesNo = (value) => (value == null ? null : value ? "Yes" : "No");

  const listingFacts = listing
    ? [
        {
          label: "Asking rent",
          value:
            listing.monthly_rent != null ? (
              <>
                {formatEuro(listing.monthly_rent)}
                <span className="wlad-listing-permonth">/mo</span>
              </>
            ) : null,
        },
        {
          label: "Rooms",
          value: listing.total_rooms != null ? listing.total_rooms : null,
        },
        {
          label: "Bathrooms",
          value: listing.bathrooms != null ? listing.bathrooms : null,
        },
        { label: "Energy label", value: listing.energy_label },
        { label: "Pets allowed", value: yesNo(listing.pets_allowed) },
        { label: "Sharing allowed", value: yesNo(listing.sharing_allowed) },
        { label: "Students allowed", value: yesNo(listing.students_allowed) },
      ].filter((fact) => fact.value != null)
    : [];

  const initials =
    `${details.first_name?.[0] || ""}${details.last_name?.[0] || ""}`
      .toUpperCase() || "?";

  const reviewedCount = Object.keys(reviews).length;

  const incomeRatio =
    offer.monthly_income != null && offer.monthly_rent
      ? (offer.monthly_income / offer.monthly_rent).toFixed(1)
      : null;

  const moveIn =
    offer.start_choice === "custom"
      ? formatDate(offer.custom_start)
      : "As soon as possible";

  const householdLabel =
    [
      household.with_partner != null
        ? household.with_partner
          ? "Couple"
          : "Single"
        : null,
      household.count != null
        ? `${household.count} ${household.count === 1 ? "person" : "people"}`
        : null,
    ]
      .filter(Boolean)
      .join(", ") || "—";

  const currentAddress =
    [
      [
        details.street,
        [
          [details.house_number, details.addition].filter(Boolean).join(""),
          details.floor,
        ]
          .filter(Boolean)
          .join("-"),
      ]
        .filter(Boolean)
        .join(" "),
      [details.postcode, details.city].filter(Boolean).join(" "),
      details.country,
    ]
      .filter(Boolean)
      .join(", ") || "—";

  // Students rent with a "2nd tenant"; employed applicants with a partner.
  const coTenantLabel =
    application?.profile === "student" ? "2nd tenant" : "Partner";
  const partnerName =
    [household.partner_first_name, household.partner_last_name]
      .filter(Boolean)
      .join(" ") ||
    household.partner_name ||
    null;

  return (
    <div className="wlad-detail">
      <div className="wlad-header">
        <div className="wlad-header-left">
          <span className="wlad-avatar">{initials}</span>
          <div>
            <div className="wlad-name-line">
              <h1 className="wlad-name">{fullName || "Unknown applicant"}</h1>
              {application.score != null && (
                <span className="wlad-score">{application.score}</span>
              )}
              <span
                className={`wlad-substatus${
                  application.is_submitted ? " wlad-substatus--submitted" : ""
                }`}
              >
                <span className="wlad-substatus-dot" />
                {application.is_submitted ? "Submitted" : "Not submitted"}
              </span>
            </div>
            <div className="wlad-meta">
              {listingAddress(listing) && (
                <>
                  <span>{listingAddress(listing)}</span>
                  <span className="wlad-meta-sep" />
                </>
              )}
              {formatDate(application.created_on) && (
                <>
                  <span>Applied {formatDate(application.created_on)}</span>
                  <span className="wlad-meta-sep" />
                </>
              )}
              <span>
                {reviewedCount}/{uploads.length} documents reviewed
              </span>
              <span className="wlad-meta-sep" />
              {applicationUrl && (
                <>
                  <a
                    className="wlad-meta-link"
                    href={applicationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open application
                    <OpenInNewIcon />
                  </a>
                  <span className="wlad-meta-sep" />
                </>
              )}
              <span className="wlad-prescan">
                <InsightsOutlinedIcon />
                AI pre-scan:
                {prescanCounts.total === 0 ? (
                  <span className="wlad-prescan-chip wlad-prescan-chip--pending">
                    no results yet
                  </span>
                ) : (
                  <>
                    {prescanCounts.blocking > 0 && (
                      <span className="wlad-prescan-chip wlad-prescan-chip--blocking">
                        <ErrorOutlineOutlinedIcon />
                        {prescanCounts.blocking} blocking
                      </span>
                    )}
                    {prescanCounts.flagged > 0 && (
                      <span className="wlad-prescan-chip wlad-prescan-chip--flagged">
                        <WarningAmberOutlinedIcon />
                        {prescanCounts.flagged} flagged
                      </span>
                    )}
                    {prescanCounts.note > 0 && (
                      <span className="wlad-prescan-chip wlad-prescan-chip--note">
                        <InfoOutlinedIcon />
                        {prescanCounts.note}{" "}
                        {prescanCounts.note === 1 ? "note" : "notes"}
                      </span>
                    )}
                    {prescanCounts.passed > 0 && (
                      <span className="wlad-prescan-chip wlad-prescan-chip--passed">
                        <CheckIcon />
                        {prescanCounts.passed} passed
                      </span>
                    )}
                  </>
                )}
              </span>
            </div>
          </div>
        </div>
        <div className="wlad-actions">
          <TriageField
            label="Priority"
            value={priority}
            options={PRIORITY_OPTIONS}
            placeholder="Not set"
            onChange={(value) => handleTriageChange("priority", value)}
            disabled={isTriageSaving}
          />
          <TriageField
            label="Assigned to"
            value={assignee}
            options={ASSIGNEE_OPTIONS}
            placeholder="Unassigned"
            onChange={(value) => handleTriageChange("assigned_to", value)}
            disabled={isTriageSaving}
          />
          <TriageField
            label="Status"
            value={status}
            options={STATUS_OPTIONS}
            onChange={(value) => handleTriageChange("status", value)}
            disabled={isTriageSaving}
          />
          {isTriageSaving ? (
            <span className="wlad-triage-spinner" aria-label="Saving..." />
          ) : triageFeedback ? (
            <span
              className={`wlad-triage-feedback wlad-triage-feedback--${triageFeedback}`}
            >
              {triageFeedback === "saved" ? (
                <>
                  <CheckIcon /> Saved
                </>
              ) : (
                <>
                  <CloseIcon /> Not saved
                </>
              )}
            </span>
          ) : null}
          <div className="wlad-actions-buttons">
            <button
              type="button"
              className="wlad-btn"
              onClick={() => setShowAskDrawer(true)}
              disabled={uploads.length === 0}
            >
              <ChatBubbleOutlineIcon /> Ask this application
            </button>
            <button type="button" className="wlad-btn" disabled>
              <MailOutlineIcon /> Email
            </button>
            <button
              type="button"
              className="wlad-btn wlad-btn--delete"
              onClick={() => setShowDeleteModal(true)}
              disabled
            >
              <DeleteOutlineIcon /> {isDeleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </div>

      <div className="wlad-tabs">
        <button
          type="button"
          className={`wlad-tab${
            activeTab === "application" ? " wlad-tab--active" : ""
          }`}
          onClick={() => setActiveTab("application")}
        >
          <VisibilityOutlinedIcon /> Application
        </button>
        <button
          type="button"
          className={`wlad-tab${
            activeTab === "documents" ? " wlad-tab--active" : ""
          }`}
          onClick={() => setActiveTab("documents")}
        >
          <AttachFileOutlinedIcon /> Documents{" "}
          <span className="wlad-tab-badge">
            {reviewedCount}/{uploads.length}
          </span>
        </button>
        <button
          type="button"
          className={`wlad-tab${
            activeTab === "cross-checks" ? " wlad-tab--active" : ""
          }`}
          onClick={() => setActiveTab("cross-checks")}
        >
          <CompareArrowsOutlinedIcon /> Cross document checks{" "}
          <span
            className={`wlad-tab-badge${
              crossCheckTotals.failed + crossCheckTotals.flagged > 0
                ? " wlad-tab-badge--issues"
                : ""
            }`}
          >
            {crossCheckTotals.passed}/
            {crossCheckTotals.total - crossCheckTotals.skipped}
          </span>
        </button>
        <button
          type="button"
          className={`wlad-tab${
            activeTab === "comments" ? " wlad-tab--active" : ""
          }`}
          onClick={() => setActiveTab("comments")}
        >
          <NotesOutlinedIcon /> Comments
          {savedComment && <span className="wlad-tab-badge">1</span>}
        </button>
      </div>

      {activeTab === "comments" && (
        <div className="wlad-card wlad-comments">
          <div className="wlad-card-title">
            <span className="wlad-card-dot" />
            Internal comments
          </div>
          <p className="wlad-comments-hint">
            Notes for the team about this application. The applicant does not
            see these.
          </p>
          <textarea
            className="wlad-comments-input"
            value={comment}
            onChange={(e) =>
              setComment(e.target.value.slice(0, COMMENT_MAX_LENGTH))
            }
            placeholder="e.g. Called the applicant on Tuesday, waiting for a new payslip."
            rows={8}
            maxLength={COMMENT_MAX_LENGTH}
            disabled={isCommentSaving}
          />
          <div className="wlad-comments-footer">
            <span
              className={`wlad-comments-count${
                comment.length >= COMMENT_MAX_LENGTH
                  ? " wlad-comments-count--limit"
                  : ""
              }`}
            >
              {comment.length}/{COMMENT_MAX_LENGTH}
            </span>
            {isCommentSaving ? (
              <span className="wlad-triage-spinner" aria-label="Saving..." />
            ) : commentFeedback ? (
              <span
                className={`wlad-triage-feedback wlad-triage-feedback--${commentFeedback}`}
              >
                {commentFeedback === "saved" ? (
                  <>
                    <CheckIcon /> Saved
                  </>
                ) : (
                  <>
                    <CloseIcon /> Not saved
                  </>
                )}
              </span>
            ) : null}
            <button
              type="button"
              className="wlad-comments-save"
              onClick={handleCommentSave}
              disabled={isCommentSaving || comment.trim() === savedComment}
            >
              Save comment
            </button>
          </div>
        </div>
      )}

      {activeTab === "cross-checks" && (
        <AdminApplicationCrossChecks
          records={crossChecks}
          applicationId={applicationId}
          onRefetch={refetchApplication}
        />
      )}

      {activeTab === "documents" && (
        <AdminApplicationDocuments
          applicationId={applicationId}
          uploads={uploads}
          reviews={reviews}
          onReview={handleReview}
          onRefetch={refetchApplication}
          applicantName={fullName}
          profile={application.profile}
          withPartner={Boolean(household.with_partner)}
        />
      )}

      {activeTab === "application" && (
      <div className="wlad-layout">
      <div className="wlad-cards">
        {savedComment && (
          <div className="wlad-card wlad-overview-comment">
            <div className="wlad-card-title">
              <span className="wlad-card-dot" />
              Internal comment
            </div>
            <p className="wlad-overview-comment-text">{savedComment}</p>
          </div>
        )}

        <div className="wlad-card">
          <div className="wlad-card-title">
            <span className="wlad-card-dot" />
            Personal details
          </div>
          <div className="wlad-columns">
            <div className="wlad-column">
              <DetailRow label="Name">{fullName || "—"}</DetailRow>
              <DetailRow label="Email">{details.email || "—"}</DetailRow>
              <DetailRow label="Mobile">{details.mobile || "—"}</DetailRow>
              <DetailRow label="Nationality">
                {nationalityLabel(details.nationality) || "—"}
              </DetailRow>
              <DetailRow label="Current address">{currentAddress}</DetailRow>
            </div>
            <div className="wlad-column">
              <DetailRow label="Profile">
                {PROFILE_LABELS[application.profile] || "—"}
              </DetailRow>
              {employment && (
                <>
                  <DetailRow label="Employer">
                    {employment.employer || "—"}
                  </DetailRow>
                  <DetailRow label="Contract">
                    {contractLengthLabel(employment.contract_length) || "—"}
                  </DetailRow>
                  <DetailRow label="Gross income">
                    {employment.monthly_income != null ? (
                      <>
                        {formatEuro(employment.monthly_income)}{" "}
                        <span className="wlad-muted">/mo</span>
                      </>
                    ) : (
                      "—"
                    )}
                  </DetailRow>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="wlad-card">
          <div className="wlad-card-title">
            <span className="wlad-card-dot" />
            Household
          </div>
          <div className="wlad-columns">
            <div className="wlad-column">
              <DetailRow label="Household">{householdLabel}</DetailRow>
              <DetailRow label="Renting together">
                {yesNo(household.with_partner) || "—"}
              </DetailRow>
              <DetailRow label="Pets">
                {household.pets != null
                  ? household.pets
                    ? `Yes${household.pet_details ? `, ${household.pet_details}` : ""}`
                    : "No"
                  : "—"}
              </DetailRow>
            </div>
            <div className="wlad-column">
              {household.with_partner && (
                <>
                  <DetailRow label={coTenantLabel}>{partnerName || "—"}</DetailRow>
                  <DetailRow label={`${coTenantLabel} contact`}>
                    {household.partner_email || "—"}
                    {household.partner_mobile && (
                      <>
                        <br />
                        {household.partner_mobile}
                      </>
                    )}
                  </DetailRow>
                  <DetailRow label={`${coTenantLabel} nationality`}>
                    {nationalityLabel(household.partner_nationality) || "—"}
                  </DetailRow>
                  {/* Only an employed applicant's partner has employment on
                      file; for students and self-employed applicants the
                      income is in the documents. */}
                  {application.profile === "employed" && (
                    <>
                      <DetailRow label="Partner employer">
                        {household.partner_employer || "—"}
                      </DetailRow>
                      <DetailRow label="Partner contract">
                        {contractLengthLabel(
                          household.partner_contract_length
                        ) || "—"}
                      </DetailRow>
                      <DetailRow label="Partner gross income">
                        {household.partner_monthly_gross_income != null ? (
                          <>
                            {formatEuro(
                              household.partner_monthly_gross_income
                            )}{" "}
                            <span className="wlad-muted">/mo</span>
                          </>
                        ) : (
                          "—"
                        )}
                      </DetailRow>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
          {household.note && (
            <div className="wlad-note">
              <span className="wlad-note-label">Note from the applicant</span>
              <p className="wlad-note-text">"{household.note}"</p>
            </div>
          )}
        </div>

        <div className="wlad-card">
          <div className="wlad-card-title">
            <span className="wlad-card-dot" />
            Applicant's offer
          </div>
          <div className="wlad-columns">
            <div className="wlad-column">
              <DetailRow label="Offer">
                {offer.monthly_rent != null ? (
                  <>
                    {formatEuro(offer.monthly_rent)}{" "}
                    <span className="wlad-muted">/mo</span>
                    {asking != null && (
                      <>
                        {" "}
                        <span className="wlad-muted-small">
                          (asking {formatEuro(asking)})
                        </span>
                      </>
                    )}
                  </>
                ) : (
                  "—"
                )}
              </DetailRow>
              <DetailRow label="Contract term">
                {offer.contract_months != null ? (
                  <>
                    {offer.contract_months}{" "}
                    <span className="wlad-muted">months</span>
                  </>
                ) : (
                  "—"
                )}
              </DetailRow>
            </div>
            <div className="wlad-column">
              <DetailRow label="Household income">
                {offer.monthly_income != null ? (
                  <>
                    {formatEuro(offer.monthly_income)}{" "}
                    <span className="wlad-muted">/mo</span>
                    {incomeRatio != null && (
                      <>
                        {" "}
                        <span className="wlad-muted-small">
                          ({incomeRatio}× offer)
                        </span>
                      </>
                    )}
                  </>
                ) : (
                  "—"
                )}
              </DetailRow>
              <DetailRow label="Move-in">{moveIn || "—"}</DetailRow>
            </div>
          </div>
        </div>
      </div>

      {listing && (
        <aside className="wlad-card wlad-listing">
          <img
            className="wlad-listing-photo"
            src={listing.main_picture_url || listing.image_url || propertyPhoto}
            alt={listingAddress(listing) || "Listing"}
          />
          <div className="wlad-listing-body">
            <div className="wlad-listing-title-line">
              <h3 className="wlad-listing-name">
                {[listing.street, listing.house_number, listing.floor]
                  .filter(Boolean)
                  .join(" ") || "Listing"}
              </h3>
            </div>
            {listingMeta && <p className="wlad-listing-meta">{listingMeta}</p>}
            <dl className="wlad-listing-facts">
              {listingFacts.map(({ label, value }) => (
                <div key={label} className="wlad-listing-fact">
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </aside>
      )}
      </div>
      )}

      {showAskDrawer && (
        <AskApplicationDrawer
          applicationId={applicationId}
          applicantName={fullName}
          firstName={details.first_name}
          docCount={uploads.length}
          thread={askThread}
          onAppend={appendAskMessage}
          onClose={closeAskDrawer}
        />
      )}

      {showDeleteModal && (
        <WLCustomModal
          title="Delete application"
          visible={true}
          setVisible={(open) => {
            if (!open) setShowDeleteModal(false);
          }}
          btnText={isDeleting ? "Deleting..." : "Delete application"}
          btnDisabled={isDeleting}
          btnOnClick={handleDelete}
          secondaryBtnText="Cancel"
        >
          <p className="wlad-delete-text">
            Are you sure you want to delete the application
            {fullName ? (
              <>
                {" "}
                from <strong>{fullName}</strong>
              </>
            ) : null}
            ? This cannot be undone.
          </p>
          {deleteError && (
            <div className="wlad-delete-error">{deleteError}</div>
          )}
        </WLCustomModal>
      )}
    </div>
  );
};

export default AdminApplicationDetail;
